import React, { useState, useRef, useMemo, useEffect } from "react";
import SearchInput from "@/components/atomics/search-input";
import WhitelistTable from "@/components/atomics/whitelist-table";
import LoadMoreButton from "@/components/atomics/load-more-button";
import { WalletListItem } from "@/services/backend";
import { useSnackbar } from "notistack";

interface WhitelistEligibleProps {
  whiteListItems: WalletListItem[];
  activeTab: string;
  onSyncWithRequests?: (eligibleWallets: string[]) => void;
  onEligibleItemsChange?: (items: WalletListItem[]) => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

const WhitelistEligible = ({
  whiteListItems,
  activeTab,
  onSyncWithRequests,
  onEligibleItemsChange,
  currentPage = 1,
  onPageChange,
}: WhitelistEligibleProps) => {
  const [eligibleItems, setEligibleItems] =
    useState<WalletListItem[]>(whiteListItems);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage] = useState(25);
  const [currentItemsShown, setCurrentItemsShown] = useState(
    Math.min(itemsPerPage, whiteListItems.length)
  );
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    if (!hasInitialized) {
      setEligibleItems(whiteListItems);
      setCurrentItemsShown(Math.min(itemsPerPage, whiteListItems.length));
      setHasInitialized(true);
    }
  }, [whiteListItems, itemsPerPage, hasInitialized]);

  useEffect(() => {
    onEligibleItemsChange?.(eligibleItems);
  }, [eligibleItems, onEligibleItemsChange]);

  useEffect(() => {
    if (currentPage && eligibleItems.length > 0) {
      const calculatedItemsShown = Math.min(
        currentPage * itemsPerPage,
        eligibleItems.length
      );
      setCurrentItemsShown(calculatedItemsShown);
    }
  }, [currentPage, itemsPerPage, eligibleItems.length]);

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return eligibleItems;
    }

    const searchLower = searchTerm.toLowerCase();
    return eligibleItems.filter(
      item =>
        item.wallet.toLowerCase().includes(searchLower) ||
        item.profile.toLowerCase().includes(searchLower)
    );
  }, [eligibleItems, searchTerm]);

  const displayedItems = useMemo(() => {
    return filteredItems.slice(0, currentItemsShown);
  }, [filteredItems, currentItemsShown]);

  useEffect(() => {
    setCurrentItemsShown(prev => {
      const maxItems = filteredItems.length;
      if (prev > maxItems && maxItems > 0) {
        return maxItems;
      }
      if (maxItems === 0) {
        return itemsPerPage;
      }
      return prev;
    });
  }, [filteredItems.length, itemsPerPage]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      if (text) {
        const wallets = text
          .split("\n")
          .map(line => line.trim())
          .filter(line => line.length > 0 && line.match(/^[A-Za-z0-9+/=_-]+$/)) // Basic validation for valid address format
          .map(wallet => ({
            wallet: wallet,
            profile: wallet,
            is_whitelisted: true,
          }));

        setEligibleItems(wallets);
        setCurrentItemsShown(Math.min(itemsPerPage, wallets.length));
        setHasInitialized(true);
      }
    };
    reader.readAsText(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteItem = (walletToDelete: string) => {
    setEligibleItems(prev =>
      prev.filter(item => item.wallet !== walletToDelete)
    );
  };

  const handleSyncWithRequests = () => {
    if (eligibleItems.length === 0) {
      enqueueSnackbar(
        "No eligible wallets uploaded. Please upload an eligible list first.",
        {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 3000,
        }
      );
      return;
    }
    const eligibleWallets = eligibleItems.map(item => item.wallet);
    onSyncWithRequests?.(eligibleWallets);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentItemsShown(itemsPerPage);
    if (onPageChange) {
      onPageChange(1);
    }
  };

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    setTimeout(() => {
      if (hasMoreItems) {
        setCurrentItemsShown(prev =>
          Math.min(prev + itemsPerPage, filteredItems.length)
        );
        if (onPageChange) {
          const nextPage = Math.ceil(
            (currentItemsShown + itemsPerPage) / itemsPerPage
          );
          onPageChange(nextPage);
        }
      } else {
        setCurrentItemsShown(itemsPerPage);
        if (onPageChange) {
          onPageChange(1);
        }
      }
      setIsLoadingMore(false);
    }, 300);
  };

  const hasMoreItems = currentItemsShown < filteredItems.length;

  return (
    <div className="w-full px-[30px] pt-[10px] text-center">
      <div className="flex justify-between gap-[18px]">
        <button
          className="flex flex-col items-center justify-center bg-primary-blue text-[20px] p-[10px] hover:opacity-80 flex-1"
          onClick={handleUploadClick}
        >
          <span>UPLOAD ELIGIBLE LIST</span>
        </button>
        <button
          className="flex flex-col items-center justify-center bg-primary-blue text-[20px] p-[10px] hover:opacity-80 flex-1"
          onClick={handleSyncWithRequests}
        >
          <span>SYNC WITH REQUESTS</span>
        </button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".txt"
        style={{ display: "none" }}
        onChange={handleFileUpload}
      />

      <SearchInput
        className="mt-[18px] mb-[18px] bg-light-gray border-none"
        placeholder="Search by wallet address..."
        value={searchTerm}
        onChange={handleSearchChange}
      />

      {eligibleItems.length === 0 ? (
        <div className="text-center py-[40px] text-gray-400 text-uppercase">
          <p>No eligible wallets uploaded</p>
          <p className="text-sm mt-2">
            Click "UPLOAD ELIGIBLE LIST" to add wallets from a text file, each
            wallet on its own line.
          </p>
        </div>
      ) : (
        <>
          <WhitelistTable
            whitelist={displayedItems}
            toggleSelect={handleDeleteItem}
            selectedWallets={new Set()}
            showCheckbox={activeTab !== "eligible"}
            showRemove={activeTab === "eligible"}
          />

          {filteredItems.length > itemsPerPage && (
            <LoadMoreButton
              isLoading={isLoadingMore}
              hasMore={hasMoreItems}
              onLoadMore={handleLoadMore}
            />
          )}
        </>
      )}
    </div>
  );
};

export default WhitelistEligible;
